import { Box, Image, Group, Burger, Drawer, ActionIcon } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { Link, useParams, useMatchRoute } from '@tanstack/react-router'
import { IconCamera, IconFileText, IconCheck, IconX } from '@tabler/icons-react'
import { useSiteContent } from '@/hooks/useSiteContent'
import styles from './GuestNavbar.module.css'

/**
 * Guest navigation bar with phoenix logo and nav links
 * Shows navigation links when guest is authenticated (has PIN)
 */
export function GuestNavbar() {
  const { content } = useSiteContent()
  const [mobileMenuOpened, { toggle, close }] = useDisclosure(false)
  const matchRoute = useMatchRoute()

  // Get PIN from route params (for /guest/logged_in/:pin routes)
  const params = useParams({ strict: false }) as { pin?: string }
  const pin = params.pin

  // Check which page is currently active
  const isInvitationActive = matchRoute({ to: '/guest/logged_in/$pin/invitation', fuzzy: true })
  const isRsvpActive = matchRoute({ to: '/guest/logged_in/$pin/rsvp', fuzzy: true })
  const isPhotosActive = matchRoute({ to: '/guest/logged_in/$pin/photos', fuzzy: true })

  // Navigation links (only shown when authenticated with PIN)
  const navLinks = pin
    ? [
        {
          to: `/guest/logged_in/${pin}/invitation`,
          label: content?.ui.Einladung || 'Einladung',
          icon: IconFileText,
          isActive: isInvitationActive,
        },
        {
          to: `/guest/logged_in/${pin}/rsvp`,
          label: content?.ui.Rückmeldung || 'Rückmeldung',
          icon: IconCheck,
          isActive: isRsvpActive,
        },
        {
          to: `/guest/logged_in/${pin}/photos`,
          label: content?.ui.Fotos || 'Fotos',
          icon: IconCamera,
          isActive: isPhotosActive,
        },
      ]
    : []

  return (
    <>
      {/* Desktop Navigation */}
      <Box className={styles.navbar}>
        {/* Logo */}
        <Box className={styles.logo}>
          <Image
            fit="contain"
            src="/assets/phoenix-icon.webp"
            alt="Phönix"
            height={67}
            radius="sm"
          />
        </Box>

        {/* Navigation Links (Desktop) */}
        {navLinks.length > 0 && (
          <Group gap="lg" className={styles.navLinks} wrap="nowrap">
            {navLinks.map((link) => {
              const Icon = link.icon
              return (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`${styles.navLink} ${link.isActive ? styles.navLinkActive : ''}`}
                >
                  <Icon size={20} />
                  <span>{link.label}</span>
                </Link>
              )
            })}
          </Group>
        )}

        {/* Spacer */}
        <Box style={{ flex: 1 }} />

        {/* Mobile Menu Button */}
        {navLinks.length > 0 && (
          <Box className={styles.mobileMenuContainer}>
            <span className={styles.mobileMenuLabel} onClick={toggle}>
              Menü
            </span>
            <Burger
              opened={mobileMenuOpened}
              onClick={toggle}
              className={styles.mobileMenuButton}
              size="md"
              color="#000000"
              aria-label="Menü öffnen"
            />
          </Box>
        )}
      </Box>

      {/* Mobile Menu Drawer */}
      <Drawer
        opened={mobileMenuOpened}
        onClose={close}
        position="right"
        size="100%"
        withCloseButton={false}
        styles={{
          header: {
            display: 'none',
          },
        }}
      >
        <Box className={styles.mobileDrawerContent}>
          {/* Mobile Navigation Links */}
          {navLinks.length > 0 && (
            <Box className={styles.mobileNavLinks}>
              {navLinks.map((link) => {
                const Icon = link.icon
                return (
                  <Link
                    key={link.to}
                    to={link.to}
                    onClick={close}
                    className={`${styles.mobileNavLink} ${link.isActive ? styles.mobileNavLinkActive : ''}`}
                  >
                    <Icon size={20} />
                    <span>{link.label}</span>
                  </Link>
                )
              })}
            </Box>
          )}

          {/* Close Button */}
          <ActionIcon
            onClick={close}
            className={styles.mobileCloseButton}
            variant="subtle"
            size="xl"
            aria-label="Schließen"
          >
            <IconX size={48} />
          </ActionIcon>
        </Box>
      </Drawer>
    </>
  )
}
